import { ServicePageTemplate } from "@/components/ServicePageTemplate";
import { Cpu, MessageSquare, Phone, Bot, Calendar, Workflow, Megaphone, Star, Mail } from "lucide-react";

const features = [
  {
    icon: Phone,
    title: "Missed-Call Text-Back",
    description: "When you can't answer, an instant SMS goes out automatically: 'Hey, we missed your call — how can we help?' No more lost leads.",
  },
  {
    icon: Bot,
    title: "AI Chatbot",
    description: "24/7 AI-powered chat on your website that answers questions, qualifies leads, and books appointments — even at 2am.",
  },
  {
    icon: MessageSquare,
    title: "Instant SMS Responses",
    description: "Every new enquiry gets an instant, personalised response within seconds. Speed wins in trades — be first to reply, every time.",
  },
  {
    icon: Workflow,
    title: "CRM Automations",
    description: "Automated follow-ups, reminders, and nurture sequences that keep leads warm and moving toward booking.",
  },
  {
    icon: Calendar,
    title: "Smart Booking System",
    description: "Online booking system that lets customers schedule quotes and appointments directly — synced to your calendar.",
  },
  {
    icon: Cpu,
    title: "Lead Tracking Dashboard",
    description: "See every lead, every response, and every booking in one dashboard. Full visibility on your sales pipeline.",
  },
];

const benefits = [
  "Never miss a lead — instant responses 24/7",
  "Win more quotes by being the first to respond",
  "Save hours on admin and manual follow-ups",
  "AI chatbot handles enquiries while you're on the tools",
  "Automated appointment booking reduces phone tag",
  "CRM keeps all leads organised in one place",
  "Nurture sequences convert more enquiries into jobs",
  "Works while you sleep — digital assistant 24/7",
];

export default function Automation() {
  return (
    <ServicePageTemplate
      title="Automation & AI Tools"
      subtitle="Missed-call text-back, CRM automations, and AI chatbots that respond instantly — 24/7."
      metaTitle="Automation & AI Tools for Trades & Service Businesses | FullScale"
      metaDescription="Missed-call text-back, AI chatbots, CRM automations and smart follow-ups for Australian trades and local service businesses. Never miss an enquiry again."
      canonicalUrl="https://fullscalemarketing.com.au/services/automation"
      badgeText="Automation & AI"
      icon={Cpu}
      introText="Trades and local service businesses don't lose work because of bad service. They lose work because someone else replied first. Our automation tools make sure every enquiry gets an instant reply, every lead gets followed up, and no opportunity falls through the cracks. Combine automation with lead generation ads and reactivation campaigns for a fully hands-free growth system."
      features={features}
      benefits={benefits}
      bestFor="Trades and local service businesses on the tools or with customers all day who keep missing calls, replying late, or losing leads to whoever answered first."
      process={[
        { step: "01", title: "Onboarding Call", text: "We map your enquiry flow — calls, web forms, socials — and where leads currently fall through." },
        { step: "02", title: "Build", text: "CRM, missed-call text-back, AI chatbot and follow-up sequences configured around your business." },
        { step: "03", title: "Launch & Train", text: "Go live, plus a quick training session so you can manage leads from your phone." },
        { step: "04", title: "Optimise Monthly", text: "We refine sequences and AI responses based on what's actually winning jobs." },
      ]}
      ctaHeading="Ready to Never Miss a Lead Again?"
      ctaText="Book a free strategy call and we'll show you how automation can save you hours and win you more jobs — on autopilot."
      relatedServices={[
        {
          icon: Megaphone,
          title: "Lead Generation Ads",
          description: "Generate a steady stream of leads, then let automation handle the follow-up.",
          href: "/services/lead-generation",
        },
        {
          icon: Star,
          title: "Reputation & Reviews",
          description: "Automate review requests after every job to build your online reputation effortlessly.",
          href: "/services/reputation",
        },
        {
          icon: Mail,
          title: "Reactivation Campaigns",
          description: "Automated email and SMS sequences to win back past customers on autopilot.",
          href: "/services/reactivation",
        },
      ]}
      faqs={[
        {
          question: "Is the AI chatbot customised to my business?",
          answer: "Yes. We train the chatbot with your services, pricing guidelines, service areas, and FAQs so it responds accurately and professionally — as if it were one of your team.",
        },
        {
          question: "What happens when someone calls and I can't answer?",
          answer: "Our missed-call text-back system instantly sends an SMS: 'Hey, we missed your call — how can we help?' This keeps the lead warm and engaged until you can follow up.",
        },
        {
          question: "Do I need technical skills to use the CRM?",
          answer: "Not at all. The CRM is simple and intuitive. You can also manage leads via a mobile app — check new enquiries, update statuses, and track your pipeline from anywhere.",
        },
        {
          question: "Can I customise the automated follow-up messages?",
          answer: "Absolutely. We set up the sequences based on your preferred tone and messaging, and you can request changes anytime. Everything is tailored to your business.",
        },
        {
          question: "How much time will automation save me?",
          answer: "Most tradies save 5–10 hours per week on admin, follow-ups, and lead management. The system handles instant responses, appointment reminders, and nurture sequences automatically.",
        },
      ]}
    />
  );
}
